import {
  BriefcaseIcon,
  FactoryIcon,
  type LucideIcon,
  PlusIcon,
  WrenchIcon,
} from "lucide-react";
import { Link } from "react-router";

import { EmptyCellButton } from "./EmptyCellButton";

import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";

import { type PortfolioMatrixJob } from "@/hooks/queries/portfolio/useGetPortfolioMatrix";
import { useUpdateSetPortfolioJobCategory } from "@/hooks/updateSets/useUpdateSetPortfolioJobCategory";
import { jobCategoryOptions, jobCategoryToLabel } from "@/lib/constants";
import { cn } from "@/lib/utils";
import { JobCategory } from "@/types";

// A distinct icon + tint per category, scannable at a glance.
const CATEGORY_DISPLAY: Record<
  JobCategory,
  { icon: LucideIcon; className: string }
> = {
  [JobCategory.PRODUCTION]: { icon: FactoryIcon, className: "text-[#2D9CDB]" },
  [JobCategory.SKILLED_TRADES]: {
    icon: WrenchIcon,
    className: "text-[#0D9488]",
  },
  [JobCategory.PROFESSIONAL]: {
    icon: BriefcaseIcon,
    className: "text-[#9B51E0]",
  },
};

// Category icon next to the job title. With an internal-config row it's an
// in-place dropdown; without one it warns and links out to create the config.
export const CategoryCell = ({ job }: { job: PortfolioMatrixJob }) => {
  /* Database CRUD Operations */
  // Update
  const { mutate: updateCategory } = useUpdateSetPortfolioJobCategory();

  /* Handlers */
  const handleSelect = (category: JobCategory, internalConfigId: string) => {
    if (category === job.category) return;
    updateCategory({
      companyId: job.companyId,
      jobId: job.id,
      internalConfigId,
      category,
    });
  };

  /* Render */
  const render = () => {
    // No config row yet — can't PATCH a category, so link out to create one.
    if (!job.internalConfigId) {
      return (
        <Tooltip>
          <TooltipTrigger asChild>
            <EmptyCellButton
              state="warning"
              asChild
              aria-label="No category — set up internal config"
            >
              <Link
                to={`/companies/${job.companyId}/jobs/${job.id}/?t=internal-config`}
                target="_blank"
                rel="noopener noreferrer"
              >
                <PlusIcon className="h-3.5 w-3.5 shrink-0 opacity-70" aria-hidden />
              </Link>
            </EmptyCellButton>
          </TooltipTrigger>
          <TooltipContent>No category — set up internal config</TooltipContent>
        </Tooltip>
      );
    }

    const internalConfigId = job.internalConfigId;
    const selected = job.category ? CATEGORY_DISPLAY[job.category] : null;
    const TriggerIcon = selected?.icon;
    const triggerLabel = job.category
      ? jobCategoryToLabel[job.category]
      : "Set job category";

    return (
      <DropdownMenu>
        <Tooltip>
          <TooltipTrigger asChild>
            <DropdownMenuTrigger asChild>
              {TriggerIcon ? (
                <Button
                  type="button"
                  variant="ghost"
                  aria-label={triggerLabel}
                  className="h-6 w-6 shrink-0 rounded p-0 hover:bg-slate-100"
                >
                  <TriggerIcon
                    className={cn("h-4 w-4", selected?.className)}
                    aria-hidden
                  />
                </Button>
              ) : (
                <EmptyCellButton
                  state="neutral"
                  aria-label={triggerLabel}
                />
              )}
            </DropdownMenuTrigger>
          </TooltipTrigger>
          <TooltipContent>{triggerLabel}</TooltipContent>
        </Tooltip>
        <DropdownMenuContent align="start" className="min-w-[180px]">
          {jobCategoryOptions.map(({ value, label }) => {
            const { icon: Icon, className } = CATEGORY_DISPLAY[value];
            return (
              <DropdownMenuItem
                key={value}
                className={cn(
                  "gap-2",
                  value === job.category && "bg-slate-50 font-medium",
                )}
                onSelect={() => {
                  handleSelect(value, internalConfigId);
                }}
              >
                <Icon
                  className={cn("h-4 w-4 shrink-0", className)}
                  aria-hidden
                />
                {label}
              </DropdownMenuItem>
            );
          })}
        </DropdownMenuContent>
      </DropdownMenu>
    );
  };

  return render();
};
